import { TestConfig, ProgrammingLanguage } from '../types';

// Programming Languages available in the typing test
export const programmingLanguages: ProgrammingLanguage[] = [
  {
    id: 'javascript',
    name: 'JavaScript',
    extension: 'js',
    icon: 'js',
    color: '#f7df1e',
    keywords: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class']
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    extension: 'ts',
    icon: 'ts',
    color: '#007acc',
    keywords: ['interface', 'type', 'enum', 'class', 'extends', 'implements', 'generic', 'any', 'void', 'never']
  },
  {
    id: 'python',
    name: 'Python',
    extension: 'py',
    icon: 'py',
    color: '#3572A5',
    keywords: ['def', 'class', 'import', 'from', 'if', 'elif', 'else', 'for', 'while', 'try']
  },
  {
    id: 'java',
    name: 'Java',
    extension: 'java',
    icon: 'java',
    color: '#b07219',
    keywords: ['public', 'private', 'class', 'interface', 'extends', 'implements', 'static', 'void', 'return', 'final']
  },
  {
    id: 'csharp',
    name: 'C#',
    extension: 'cs',
    icon: 'cs',
    color: '#178600',
    keywords: ['using', 'namespace', 'class', 'interface', 'public', 'private', 'static', 'void', 'async', 'await']
  },
  {
    id: 'go',
    name: 'Go',
    extension: 'go',
    icon: 'go',
    color: '#00ADD8',
    keywords: ['func', 'package', 'import', 'type', 'struct', 'interface', 'map', 'chan', 'go', 'defer']
  },
  {
    id: 'ruby',
    name: 'Ruby',
    extension: 'rb',
    icon: 'rb',
    color: '#CC342D',
    keywords: ['def', 'class', 'module', 'include', 'require', 'attr', 'if', 'elsif', 'end', 'do']
  },
  {
    id: 'rust',
    name: 'Rust',
    extension: 'rs',
    icon: 'rs',
    color: '#DEA584',
    keywords: ['fn', 'struct', 'enum', 'impl', 'pub', 'use', 'let', 'mut', 'match', 'unsafe']
  },
  {
    id: 'php',
    name: 'PHP',
    extension: 'php',
    icon: 'php',
    color: '#4F5D95',
    keywords: ['function', 'class', 'public', 'private', 'protected', 'namespace', 'use', 'echo', 'require', 'include']
  },
  {
    id: 'bash',
    name: 'Bash',
    extension: 'sh',
    icon: 'sh',
    color: '#89e051',
    keywords: ['if', 'then', 'else', 'fi', 'for', 'while', 'do', 'done', 'function', 'echo']
  }
];

// Extended word list for more varied text - common English words for basic typing
export const words = [
  'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'I', 'it', 'for', 
  'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at', 'this', 'but', 'his', 
  'by', 'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my',
  'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out', 'if',
  'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like',
  'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year', 'your',
  'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look',
  'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use', 'two',
  'how', 'our', 'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because',
  'any', 'these', 'give', 'day', 'most', 'us', 'is', 'water', 'long', 'very',
  'through', 'always', 'world', 'each', 'life', 'where', 'important', 'never', 'together',
  'another', 'often', 'different', 'learn', 'change', 'family', 'both', 'between',
  'against', 'place', 'happen', 'while', 'might', 'however', 'again', 'under', 'during',
  'before', 'within', 'around', 'every', 'still', 'three', 'without', 'small', 'large',
  'though', 'little', 'system', 'number', 'always', 'become', 'process', 'better', 'question',
  'write', 'type', 'fast', 'speed', 'skill', 'improve', 'practice', 'keyboard', 'finger',
  'motion', 'quick', 'rhythm', 'pattern', 'memory', 'muscle', 'focus', 'accuracy', 'error',
  'correct', 'position', 'posture', 'wrist', 'relax', 'breathe', 'flow', 'zone', 'habit',
  'develop', 'master', 'expert', 'novice', 'beginner', 'advanced', 'progress', 'track',
  'measure', 'result', 'goal', 'target', 'achieve', 'success', 'failure', 'learn'
];



// Programming language-specific code snippets for coding practice
const codeSnippets = {
  "javascript": [
    // Basic functions
    `function fibonacci(n) {
  if (n <= 1) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}`,
    // ES6+ features
    `const users = await fetch("/api/users")
  .then(response => response.json())
  .catch(error => console.error(error));`,
    // React component
    `class Component extends React.Component {
  constructor(props) {
    super(props);
    this.state = { count: 0 };
  }

  increment = () => {
    this.setState(state => ({ count: state.count + 1 }));
  }

  render() {
    return (
      <div>
        <p>Count: {this.state.count}</p>
        <button onClick={this.increment}>Increment</button>
      </div>
    );
  }
}`,
    // Modern JS functions
    `const filterAndMap = (array, filterPredicate, mapFunction) => {
  return array
    .filter(filterPredicate)
    .map(mapFunction);
};

const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const evenSquares = filterAndMap(
  numbers,
  num => num % 2 === 0,
  num => num * num
);`,
    // Closures and scope
    `function createCounter() {
  let count = 0;
  
  return {
    increment: () => {
      count += 1;
      return count;
    },
    decrement: () => {
      count -= 1;
      return count;
    },
    getCount: () => count
  };
}

const counter = createCounter();
console.log(counter.increment()); // 1
console.log(counter.increment()); // 2
console.log(counter.decrement()); // 1`,
    // Array operations
    `const inventory = [
  {name: 'apples', quantity: 2},
  {name: 'bananas', quantity: 0},
  {name: 'oranges', quantity: 5}
];

const result = inventory.find(item => item.name === 'oranges');
console.log(result); // {name: 'oranges', quantity: 5}

// Using reduce to calculate total
const total = inventory.reduce((sum, item) => sum + item.quantity, 0);
console.log(total); // 7`,
    // Promises
    `function fetchUserData(userId) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (userId > 0) {
        resolve({
          id: userId,
          name: 'User ' + userId,
          email: \`user\${userId}@example.com\`
        });
      } else {
        reject(new Error('Invalid user ID'));
      }
    }, 1000);
  });
}

async function displayUserInfo(userId) {
  try {
    const user = await fetchUserData(userId);
    console.log(\`Name: \${user.name}, Email: \${user.email}\`);
  } catch (error) {
    console.error('Failed to fetch user:', error);
  }
}`,
    // Event handling
    `document.getElementById('myButton').addEventListener('click', function(event) {
  event.preventDefault();
  
  const formData = new FormData(document.getElementById('myForm'));
  const data = Object.fromEntries(formData.entries());
  
  fetch('/api/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  .then(response => response.json())
  .then(result => {
    console.log('Success:', result);
    document.getElementById('status').textContent = 'Form submitted!';
  })
  .catch(error => {
    console.error('Error:', error);
    document.getElementById('status').textContent = 'Submission failed!';
  });
});`,
    // Destructuring and spread
    `const person = {
  name: 'John Doe',
  age: 30,
  address: {
    city: 'New York',
    country: 'USA'
  },
  hobbies: ['reading', 'music', 'sports']
};

const { name, age, address: { city }, hobbies: [firstHobby, ...restHobbies] } = person;

console.log(name); // John Doe
console.log(city); // New York
console.log(firstHobby); // reading
console.log(restHobbies); // ['music', 'sports']`
  ],
  "python": [
    // Basic functions
    `def fibonacci(n):
    """Calculate the Fibonacci number of n."""
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

# Calculate the first 10 Fibonacci numbers
for i in range(10):
    print(f"Fibonacci({i}) = {fibonacci(i)}")`,
    
    // List comprehensions
    `# List comprehension examples
numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Square all numbers
squares = [num ** 2 for num in numbers]
print(squares)

# Get even numbers only
evens = [num for num in numbers if num % 2 == 0]
print(evens)`,
    // Algorithms
    `def quicksort(arr):
    if len(arr) <= 1:
        return arr
    pivot = arr[len(arr) // 2]
    left = [x for x in arr if x < pivot]
    middle = [x for x in arr if x == pivot]
    right = [x for x in arr if x > pivot]
    return quicksort(left) + middle + quicksort(right)`,
    // Data visualization
    `import matplotlib.pyplot as plt
import numpy as np

x = np.linspace(0, 10, 100)
y = np.sin(x)

plt.plot(x, y)
plt.title('Sine Wave')
plt.xlabel('x')
plt.ylabel('sin(x)')
plt.show()`,
    // Flask web app
    `from flask import Flask, request, jsonify

app = Flask(__name__)

@app.route('/api/items', methods=['GET'])
def get_items():
    items = [
        {'id': 1, 'name': 'Item 1'},
        {'id': 2, 'name': 'Item 2'}
    ]
    return jsonify(items)

@app.route('/api/items', methods=['POST'])
def create_item():
    data = request.json
    # Save to database (omitted)
    return jsonify({'success': True}), 201

if __name__ == '__main__':
    app.run(debug=True)`,
    // Classes and OOP
    `class Person:
    def __init__(self, name, age):
        self.name = name
        self.age = age
        
    def greet(self):
        return f"Hello, my name is {self.name} and I am {self.age} years old."
        
    @property
    def is_adult(self):
        return self.age >= 18
        
    @classmethod
    def from_birth_year(cls, name, birth_year):
        import datetime
        current_year = datetime.datetime.now().year
        age = current_year - birth_year
        return cls(name, age)
        
# Create a new person
person = Person("Alice", 25)
print(person.greet())
print(f"Is adult: {person.is_adult}")

# Create using class method
person2 = Person.from_birth_year("Bob", 1995)
print(person2.greet())`,
    // Decorators
    `import functools
import time

# A decorator for timing function execution
def timer(func):
    @functools.wraps(func)
    def wrapper(*args, **kwargs):
        start_time = time.time()
        result = func(*args, **kwargs)
        end_time = time.time()
        print(f"Function {func.__name__} took {end_time - start_time:.4f} seconds to run")
        return result
    return wrapper

# Using the decorator
@timer
def calculate_sum(n):
    """Calculate the sum of numbers from 0 to n"""
    return sum(range(n + 1))

# Call our decorated function
result = calculate_sum(1000000)
print(f"Sum result: {result}")`,
    
    // Data analysis
    `import pandas as pd
import numpy as np

# Create a sample DataFrame
data = {
    'name': ['Alice', 'Bob', 'Charlie', 'David', 'Eva'],
    'age': [25, 30, 35, 40, 45],
    'salary': [50000, 60000, 70000, 80000, 90000],
    'department': ['HR', 'IT', 'Finance', 'IT', 'Marketing']
}

df = pd.DataFrame(data)
print(df.head())

# Basic statistics
print("\\nStatistics:")
print(df.describe())

# Group by department
print("\\nAverage salary by department:")
dept_avg = df.groupby('department')['salary'].mean()
print(dept_avg)

# Add a new column
df['experience'] = [3, 5, 7, 10, 12]
print("\\nDataFrame with experience column:")
print(df)`
  ],
  "typescript": [
    // Interfaces
    `interface User {
  id: number;
  name: string;
  email: string;
  age?: number; // Optional property
  readonly createdAt: Date;
}

function createUser(name: string, email: string): User {
  return {
    id: Math.floor(Math.random() * 10000),
    name,
    email,
    createdAt: new Date()
  };
}

const newUser = createUser("Alice", "alice@example.com");
console.log(newUser.id);`,
    // Generic types
    `function identity<T>(arg: T): T {
  return arg;
}

const a = identity<number>(42);
const b = identity("hello");

interface Collection<T> {
  add(item: T): void;
  remove(item: T): boolean;
  items(): T[];
}

class List<T> implements Collection<T> {
  private elements: T[] = [];
  
  add(item: T): void {
    this.elements.push(item);
  }
  
  remove(item: T): boolean {
    const index = this.elements.indexOf(item);
    if (index > -1) {
      this.elements.splice(index, 1);
      return true;
    }
    return false;
  }
  
  items(): T[] {
    return [...this.elements];
  }
}`,
    // Union, intersection types
    `type StringOrNumber = string | number;

function formatValue(value: StringOrNumber): string {
  if (typeof value === "string") {
    return value.toUpperCase();
  } else {
    return value.toFixed(2);
  }
}

interface HasName {
  name: string;
}

interface HasAge {
  age: number;
}

type Person = HasName & HasAge;

function greet(person: Person): string {
  return \`Hello, \${person.name}! You are \${person.age} years old.\`;
}

const john: Person = { name: "John", age: 30 };
console.log(greet(john));`,
    // Classes and inheritance
    `abstract class Animal {
  protected name: string;
  
  constructor(name: string) {
    this.name = name;
  }
  
  abstract makeSound(): void;
  
  move(distance: number = 0): void {
    console.log(\`\${this.name} moved \${distance}m.\`);
  }
}

class Dog extends Animal {
  constructor(name: string) {
    super(name);
  }
  
  makeSound(): void {
    console.log("Woof! Woof!");
  }
  
  fetch(): void {
    console.log(\`\${this.name} is fetching the ball!\`);
  }
}

const dog = new Dog("Rex");
dog.makeSound();
dog.move(10);
dog.fetch();`,
    // Utility types
    `interface Todo {
  title: string;
  description: string;
  completed: boolean;
  createdAt: Date;
}

// Using built-in utility types
type TodoPreview = Omit<Todo, "description" | "createdAt">;
type ReadonlyTodo = Readonly<Todo>;

const todo: Todo = {
  title: "Learn TypeScript",
  description: "Study advanced type features",
  completed: false,
  createdAt: new Date()
};

const todoPreview: TodoPreview = {
  title: todo.title,
  completed: todo.completed
};

function updateTodo(todo: Todo, fieldsToUpdate: Partial<Todo>): Todo {
  return { ...todo, ...fieldsToUpdate };
}

const updatedTodo = updateTodo(todo, { completed: true });`,
    // Type guards and narrowing
    `type Shape = 
  | { kind: "circle"; radius: number }
  | { kind: "rectangle"; width: number; height: number }
  | { kind: "square"; size: number };

function calculateArea(shape: Shape): number {
  switch (shape.kind) {
    case "circle":
      return Math.PI * shape.radius ** 2;
    case "rectangle":
      return shape.width * shape.height;
    case "square":
      return shape.size ** 2;
    default:
      const _exhaustiveCheck: never = shape;
      throw new Error(\`Unhandled shape: \${_exhaustiveCheck}\`);
  }
}

function isCircle(shape: Shape): shape is { kind: "circle"; radius: number } {
  return shape.kind === "circle";
}

const myShape: Shape = { kind: "rectangle", width: 10, height: 5 };
console.log(calculateArea(myShape));`
  ],
  "java": [
    // Basic class
    `public class Person {
    private String name;
    private int age;
    
    public Person(String name, int age) {
        this.name = name;
        this.age = age;
    }
    
    public String getName() {
        return name;
    }
    
    public void setName(String name) {
        this.name = name;
    }
    
    public int getAge() {
        return age;
    }
    
    public void setAge(int age) {
        this.age = age;
    }
    
    @Override
    public String toString() {
        return "Person{name='" + name + "', age=" + age + "}";
    }
}`,
    // Inheritance and polymorphism
    `// Base class
public abstract class Shape {
    protected String color;
    
    public Shape(String color) {
        this.color = color;
    }
    
    public String getColor() {
        return color;
    }
    
    // Abstract method
    public abstract double calculateArea();
    
    @Override
    public String toString() {
        return "Shape{color='" + color + "'}";
    }
}

// Circle subclass
public class Circle extends Shape {
    private double radius;
    
    public Circle(String color, double radius) {
        super(color);
        this.radius = radius;
    }
    
    @Override
    public double calculateArea() {
        return Math.PI * radius * radius;
    }
    
    @Override
    public String toString() {
        return "Circle{color='" + color + "', radius=" + radius + "}";
    }
}`
  ],
  "csharp": [
    // Basic class and properties
    `using System;

class Person
{
    // Auto-implemented properties
    public string Name { get; set; }
    public int Age { get; set; }
    
    // Property with backing field
    private string _email;
    public string Email
    {
        get { return _email; }
        set 
        { 
            if (value.Contains("@"))
                _email = value;
            else
                throw new ArgumentException("Invalid email format");
        }
    }
    
    // Constructor
    public Person(string name, int age, string email)
    {
        Name = name;
        Age = age;
        Email = email;
    }
    
    // Method
    public void Introduce()
    {
        Console.WriteLine($"Hello, my name is {Name}, I am {Age} years old.");
        Console.WriteLine($"You can contact me at: {Email}");
    }
}`,
    // Java Collections
    `import java.util.*;

public class CollectionsExample {
    public static void main(String[] args) {
        // ArrayList
        List<String> namesList = new ArrayList<>();
        namesList.add("John");
        namesList.add("Alice");
        namesList.add("Bob");
        
        System.out.println("List: " + namesList);
        
        // HashSet
        Set<Integer> numbersSet = new HashSet<>();
        numbersSet.add(1);
        numbersSet.add(2);
        numbersSet.add(2); // Duplicate, won't be added
        numbersSet.add(3);
        
        System.out.println("Set: " + numbersSet);
        
        // HashMap
        Map<String, Integer> ageMap = new HashMap<>();
        ageMap.put("John", 25);
        ageMap.put("Alice", 30);
        ageMap.put("Bob", 28);
        
        for (Map.Entry<String, Integer> entry : ageMap.entrySet()) {
            System.out.println(entry.getKey() + " is " + entry.getValue() + " years old");
        }
    }
}`,
    // LINQ example
    `using System;
using System.Linq;
using System.Collections.Generic;

class LinqExample
{
    static void Main()
    {
        List<int> numbers = new List<int> { 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 };
        
        // Basic LINQ operations
        var evenNumbers = numbers.Where(n => n % 2 == 0);
        Console.WriteLine("Even numbers: " + string.Join(", ", evenNumbers));
        
        var squaredNumbers = numbers.Select(n => n * n);
        Console.WriteLine("Squared: " + string.Join(", ", squaredNumbers));
        
        int sum = numbers.Sum();
        double average = numbers.Average();
        Console.WriteLine($"Sum: {sum}, Average: {average}");
        
        // More complex LINQ
        var processed = numbers
            .Where(n => n > 3)
            .OrderByDescending(n => n)
            .Select(n => $"Number: {n}");
            
        Console.WriteLine("Processed: " + string.Join(", ", processed));
    }
}`,
    // Async/await
    `using System;
using System.Threading.Tasks;

class AsyncExample
{
    static async Task Main()
    {
        Console.WriteLine("Starting...");
        
        // Call async method
        string result = await FetchDataAsync();
        Console.WriteLine($"Result: {result}");
        
        // Multiple async tasks
        Task<int> task1 = CalculateAsync(10);
        Task<int> task2 = CalculateAsync(20);
        
        // Wait for both tasks to complete
        await Task.WhenAll(task1, task2);
        
        int sum = task1.Result + task2.Result;
        Console.WriteLine($"Sum of results: {sum}");
        
        Console.WriteLine("Completed!");
    }
    
    static async Task<string> FetchDataAsync()
    {
        // Simulate API call
        await Task.Delay(2000); // 2 second delay
        return "Data from API";
    }
    
    static async Task<int> CalculateAsync(int input)
    {
        await Task.Delay(1000); // 1 second delay
        return input * input;
    }
}`,
    // Interfaces and inheritance
    `using System;

// Interface definition
interface IShape
{
    double CalculateArea();
    void Display();
}

// Abstract base class
abstract class Shape : IShape
{
    protected string Color;
    
    public Shape(string color)
    {
        Color = color;
    }
    
    // Abstract method
    public abstract double CalculateArea();
    
    // Virtual method
    public virtual void Display()
    {
        Console.WriteLine($"This is a {Color} shape.");
    }
}

// Concrete class
class Circle : Shape
{
    private double Radius;
    
    public Circle(string color, double radius) : base(color)
    {
        Radius = radius;
    }
    
    public override double CalculateArea()
    {
        return Math.PI * Radius * Radius;
    }
    
    public override void Display()
    {
        base.Display();
        Console.WriteLine($"This is a {Color} circle with radius {Radius}");
        Console.WriteLine($"Area: {CalculateArea():F2}");
    }
}`
  ],
  "go": [
    // Basic syntax
    `package main

import (
	"fmt"
	"time"
)

func main() {
	// Variables
	var name string = "John"
	age := 30 // Short variable declaration
	
	// Constants
	const PI = 3.14159
	
	fmt.Println("Hello,", name)
	fmt.Printf("You are %d years old\n", age)
	fmt.Printf("PI value: %.2f\n", PI)
	
	// Control structures
	if age >= 18 {
		fmt.Println("You are an adult")
	} else {
		fmt.Println("You are a minor")
	}
	
	// For loop
	fmt.Println("Counting to 5:")
	for i := 1; i <= 5; i++ {
		fmt.Println(i)
	}
	
	// Time
	fmt.Println("Current time:", time.Now())
}`,
    // Structs and methods
    `package main

import (
	"fmt"
	"math"
)

// Person struct
type Person struct {
	Name string
	Age  int
}

// Method for Person
func (p Person) Greet() string {
	return fmt.Sprintf("Hello, my name is %s and I am %d years old", p.Name, p.Age)
}

// Shape interface
type Shape interface {
	Area() float64
	Perimeter() float64
}

// Rectangle struct
type Rectangle struct {
	Width  float64
	Height float64
}

// Implement Shape interface for Rectangle
func (r Rectangle) Area() float64 {
	return r.Width * r.Height
}

func (r Rectangle) Perimeter() float64 {
	return 2 * (r.Width + r.Height)
}

// Circle struct
type Circle struct {
	Radius float64
}

// Implement Shape interface for Circle
func (c Circle) Area() float64 {
	return math.Pi * c.Radius * c.Radius
}

func (c Circle) Perimeter() float64 {
	return 2 * math.Pi * c.Radius
}

func main() {
	// Create a Person
	p := Person{Name: "Alice", Age: 30}
	fmt.Println(p.Greet())
	
	// Use interfaces
	r := Rectangle{Width: 10, Height: 5}
	c := Circle{Radius: 7}
	
	shapes := []Shape{r, c}
	
	for _, s := range shapes {
		fmt.Printf("Area: %.2f\n", s.Area())
		fmt.Printf("Perimeter: %.2f\n", s.Perimeter())
	}
}`,
    // Goroutines and channels
    `package main

import (
	"fmt"
	"time"
)

func worker(id int, jobs <-chan int, results chan<- int) {
	for j := range jobs {
		fmt.Printf("Worker %d started job %d\n", id, j)
		time.Sleep(time.Second) // Simulate work
		fmt.Printf("Worker %d finished job %d\n", id, j)
		results <- j * 2
	}
}

func main() {
	jobs := make(chan int, 5)
	results := make(chan int, 5)
	
	// Start workers
	for w := 1; w <= 3; w++ {
		go worker(w, jobs, results)
	}
	
	// Send jobs
	for j := 1; j <= 5; j++ {
		jobs <- j
	}
	close(jobs)
	
	// Collect results
	for i := 0; i < 5; i++ {
		result := <-results
		fmt.Println("Result:", result)
	}
}`
  ],
  "ruby": [
    // Basic syntax
    `# Ruby classes and inheritance
class Person
  attr_accessor :name, :age
  
  def initialize(name, age)
    @name = name
    @age = age
  end
  
  def greet
    "Hello, my name is #{@name} and I am #{@age} years old."
  end
  
  def adult?
    @age >= 18
  end
end

# Child class
class Student < Person
  attr_accessor :grade
  
  def initialize(name, age, grade)
    super(name, age)
    @grade = grade
  end
  
  def greet
    "#{super} I am in grade #{@grade}."
  end
end

# Create instances
p = Person.new("John", 30)
puts p.greet
puts "Adult? #{p.adult?}"

s = Student.new("Alice", 16, 10)
puts s.greet
puts "Adult? #{s.adult?}"`,
    // Ruby blocks and iterators
    `# Ruby blocks, iterators, and yield
def repeat(count)
  count.times do |i|
    yield i if block_given?
  end
end

# Using the method with a block
repeat(5) do |i|
  puts "Iteration #{i + 1}"
end

# Array manipulation with blocks
numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Map (transform elements)
squares = numbers.map { |n| n * n }
puts "Squares: #{squares}"

# Select (filter elements)
evens = numbers.select { |n| n.even? }
puts "Even numbers: #{evens}"

# Reduce (aggregate elements)
sum = numbers.reduce(0) { |total, n| total + n }
puts "Sum: #{sum}"

# Each with index
numbers.each_with_index do |num, idx|
  puts "numbers[#{idx}] = #{num}"
end`,
    // Ruby metaprogramming
    `# Ruby metaprogramming
class Dynamic
  # Define methods dynamically
  def self.create_getter_and_setter(attribute)
    # Define getter
    define_method(attribute) do
      instance_variable_get("@#{attribute}")
    end
    
    # Define setter
    define_method("#{attribute}=") do |value|
      instance_variable_set("@#{attribute}", value)
    end
  end
  
  # Call method_missing when a method is not found
  def method_missing(method_name, *args)
    puts "Called undefined method: #{method_name} with #{args.inspect}"
    "Method not found"
  end
end

# Create class with dynamic methods
class Person < Dynamic
  # Create getters and setters dynamically
  create_getter_and_setter :name
  create_getter_and_setter :age
end

# Use the dynamically created methods
person = Person.new
person.name = "Ruby Master"
person.age = 30

puts "Name: #{person.name}"
puts "Age: #{person.age}"

# Call undefined method
person.unknown_method("test")`
  ],
  "rust": [
    // Basic syntax
    `// Basic Rust syntax
fn main() {
    // Variables and mutability
    let x = 5;
    println!("The value of x is: {}", x);
    
    // x = 6; // This would cause an error, x is immutable
    
    let mut y = 10;
    println!("The value of y is: {}", y);
    
    y = 20; // This works because y is mutable
    println!("The updated value of y is: {}", y);
    
    // Constants
    const MAX_POINTS: u32 = 100_000;
    println!("The maximum points are: {}", MAX_POINTS);
    
    // Shadowing
    let spaces = "   ";
    let spaces = spaces.len();
    println!("Spaces length: {}", spaces);
    
    // Basic types
    let integer: i32 = 42;
    let float: f64 = 3.14;
    let boolean: bool = true;
    let character: char = 'A';
    
    println!("Integer: {}, Float: {}, Boolean: {}, Character: {}", 
             integer, float, boolean, character);
}`,
    // Functions
    `// Functions and control flow
fn main() {
    println!("Fibonacci sequence:");
    for n in 0..10 {
        println!("Fibonacci({}) = {}", n, fibonacci(n));
    }
    
    // Using closures
    let square = |x| x * x;
    println!("The square of 5 is: {}", square(5));
}

// Recursive function
fn fibonacci(n: u32) -> u32 {
    if n <= 1 {
        return n;
    }
    fibonacci(n - 1) + fibonacci(n - 2)
}`,
    // Structs and enums
    `// Structs and enums
struct Person {
    name: String,
    age: u32,
}

impl Person {
    // Associated function (constructor)
    fn new(name: &str, age: u32) -> Person {
        Person {
            name: String::from(name),
            age,
        }
    }
    
    // Method
    fn greet(&self) -> String {
        format!("Hello, my name is {} and I am {} years old.", self.name, self.age)
    }
}

// Enum with data
enum Shape {
    Circle(f64),            // radius
    Rectangle(f64, f64),    // width, height
}

impl Shape {
    // Method to calculate area
    fn area(&self) -> f64 {
        match *self {
            Shape::Circle(radius) => std::f64::consts::PI * radius * radius,
            Shape::Rectangle(width, height) => width * height,
        }
    }
}

fn main() {
    let person = Person::new("Alice", 30);
    println!("{}", person.greet());
    
    let shape = Shape::Circle(5.0);
    println!("The area of the shape is: {}", shape.area());
}`,
    // Traits and generics
    `// Traits and generics
trait Shape {
    fn area(&self) -> f64;
}

struct Circle {
    radius: f64,
}

struct Rectangle {
    width: f64,
    height: f64,
}

// Implementing trait for Circle
impl Shape for Circle {
    fn area(&self) -> f64 {
        std::f64::consts::PI * self.radius * self.radius
    }
}

// Implementing trait for Rectangle
impl Shape for Rectangle {
    fn area(&self) -> f64 {
        self.width * self.height
    }
}

// Generic function with trait bound
fn print_area<T: Shape>(shape: T) {
    println!("The area is: {}", shape.area());
}

fn main() {
    let circle = Circle { radius: 5.0 };
    let rectangle = Rectangle { width: 4.0, height: 6.0 };
    
    print_area(circle);
    print_area(rectangle);
}`,
    // Lifetimes
    `// Lifetimes
struct Book<'a> {
    title: &'a str,
    author: &'a str,
}

fn main() {
    let s1 = String::from("The Catcher in the Rye");
    let s2 = String::from("J.D. Salinger");
    
    // Lifetime annotation: 'a
    let book: Book = Book {
        title: s1.as_str(),
        author: s2.as_str(),
    };
    
    println!("Book title: {}, Author: {}", book.title, book.author);
}`
  ],
  "php": [
    // Basic syntax
    `<?php
// Basic PHP syntax
function sayHello($name) {
    return "Hello, $name!";
}

$greeting = sayHello("World");
echo $greeting . "<br>";

// Variables and types
$integer = 42;
$float = 3.14;
$string = "PHP is fun";
$boolean = true;
$array = ["Apple", "Banana", "Orange"];
$null = null;

echo "Integer: $integer<br>";
echo "Float: $float<br>";
echo "String: $string<br>";
echo "Boolean: " . ($boolean ? "true" : "false") . "<br>";
echo "Array: " . implode(", ", $array) . "<br>";
echo "Null: " . ($null === null ? "null" : "not null") . "<br>";

// Conditionals
$hour = date("H");
if ($hour < 12) {
    echo "Good morning!<br>";
} elseif ($hour < 18) {
    echo "Good afternoon!<br>";
} else {
    echo "Good evening!<br>";
}

// Loops
echo "Counting to 5: ";
for ($i = 1; $i <= 5; $i++) {
    echo "$i ";
}
echo "<br>";

// Array iteration
echo "Fruits: ";
foreach ($array as $key => $fruit) {
    echo "$key:$fruit ";
}
?>`,
    // Functions
    `<?php
// PHP functions
function greet($name) {
    return "Hello, " . $name . "!";
}

echo greet("John");


function add($a, $b = 10) {
    return $a + $b;
}

echo add(5);
echo add(5, 15);
?>`,
    // Control structures
    `<?php
// PHP control structures
$age = 20;

if ($age >= 18) {
    echo "You are an adult.";
} else {
    echo "You are a minor.";
}


$colors = array("red", "green", "blue");

foreach ($colors as $color) {
    echo "Color: " . $color;
}


$i = 1;

do {
    echo "Iteration: " . $i;
    $i++;
} while ($i <= 5);
?>`,
    // Classes and objects
    `<?php
// PHP classes and objects
class Person {
    public $name;
    public $age;
    
    function __construct($name, $age) {
        $this->name = $name;
        $this->age = $age;
    }
    
    function greet() {
        return "Hello, my name is " . $this->name;
    }
}

$john = new Person("John", 30);
echo $john->greet();
?>`,
    // PHP OOP
    `<?php
// PHP OOP example
class Person {
    // Properties
    private $name;
    private $age;
    
    // Constructor
    public function __construct($name, $age) {
        $this->name = $name;
        $this->age = $age;
    }
    
    // Getters and setters
    public function getName() {
        return $this->name;
    }
    
    public function setName($name) {
        $this->name = $name;
    }
    
    public function getAge() {
        return $this->age;
    }
    
    public function setAge($age) {
        $this->age = $age;
    }
    
    // Method
    public function greet() {
        return "Hello, my name is " . $this->name . " and I am " . $this->age . " years old.";
    }
    
    // Static method
    public static function getClassName() {
        return "Person";
    }
}

// Inheritance
class Student extends Person {
    private $grade;
    
    public function __construct($name, $age, $grade) {
        parent::__construct($name, $age);
        $this->grade = $grade;
    }
    
    public function getGrade() {
        return $this->grade;
    }
    
    // Method override
    public function greet() {
        return parent::greet() . " I am in grade " . $this->grade . ".";
    }
}

// Usage
$person = new Person("John", 30);
echo $person->greet() . "<br>";

$student = new Student("Alice", 15, 10);
echo $student->greet() . "<br>";

echo "Class name: " . Person::getClassName() . "<br>";
?>`
  ],
  "bash": [
    // Basic commands
    `# Bash basic commands
echo "Hello, World!"

# Variables
name="John"
age=30
is_student=false

echo "Name: $name, Age: $age"

# Arrays
fruits=("apple" "banana" "cherry")
echo "First fruit: \${fruits[0]}"


# Associative array (dictionary)
declare -A person
person[name]="Alice"
person[age]=28
person[is_student]=true

echo "Name: \${person[name]}, Age: \${person[age]}"

# Control structures
if [ $age -ge 18 ]; then
    echo "You are an adult"
else
    echo "You are a minor"
fi

# Loops
echo "Counting to 5:"
for i in {1..5}; do
    echo "Number: $i"
done

# While loop
count=1
while [ $count -le 5 ]; do
    echo "Count: $count"
    ((count++))
done
`,
    // Functions
    `# Bash functions

# Simple function
function hello() {
    echo "Hello, World!"
}

# Function with arguments
function greet() {
    echo "Hello, $1!"
}

# Function with return value
function add() {
    local a=$1
    local b=\${2:-10}
    echo $((a + b))
}

# Call functions
hello
greet "John"
sum=$(add 5)
echo "Sum with default: $sum"
sum=$(add 5 15)
echo "Sum with custom value: $sum"`,
    // String manipulation
    `# String manipulation
text="Hello World from Bash"

# Length
echo "Length: \${#text}"

# Substring
echo "Substring: \${text:7:5}"

# Replace
echo "Replace: \${text/World/Bash}"

# Uppercase and lowercase
echo "Uppercase: \${text^^}"
echo "Lowercase: \${text,,}"

# Remove prefix/suffix
echo "Remove prefix: \${text#Hello }"
echo "Remove suffix: \${text% from Bash}"

# Check if string contains a substring
if [[ $text == *"World"* ]]; then
    echo "Text contains 'World'"
fi`
  ]
};

export function generateTestText(config: TestConfig): { text: string; words: string[] } { // NOSONAR
  let text = '';
  let wordsArray: string[] = [];
  
  // For custom text mode (premium only), use the provided text
  if (config.mode === 'custom' && config.customText) {
    text = config.customText;
    wordsArray = text.split(/\s+/);
    return { text, words: wordsArray };
  }
  
  // Use all available words from the word list
  const wordPool = words;

  switch (config.mode) {
    case 'time':
    case 'words': {
      // Generate word-based text
      const targetWordCount = config.mode === 'words' ? config.wordCount! : 200;
      const selectedWords: string[] = [];
      
      // Create more natural text with varied sentence lengths
      let currentSentenceLength = 0;
      const maxSentenceLength = 12;
      
      for (let i = 0; i < targetWordCount; i++) {
        const randomWord = wordPool[Math.floor(Math.random() * wordPool.length)];
        selectedWords.push(randomWord);
        currentSentenceLength++;
        
        // Add punctuation if enabled
        if (config.punctuation) {
          // End sentence with some probability or if we've reached max sentence length
          if (currentSentenceLength >= maxSentenceLength || 
              (currentSentenceLength > 3 && Math.random() < 0.2)) {
            
            const lastIndex = selectedWords.length - 1;
            const punctuations = ['.', '.', '.', '?', '!', ';', ','];
            const randomPunctuation = punctuations[Math.floor(Math.random() * punctuations.length)];
            
            // Don't use semicolons or commas to end sentences
            if (randomPunctuation === ';' || randomPunctuation === ',') {
              if (i < targetWordCount - 1 && Math.random() < 0.7) {
                selectedWords[lastIndex] = selectedWords[lastIndex] + randomPunctuation;
              }
            } else {
              selectedWords[lastIndex] = selectedWords[lastIndex] + randomPunctuation;
              currentSentenceLength = 0;
              
              // Capitalize next word if not at the end
              if (i < targetWordCount - 1) {
                const nextWord = wordPool[Math.floor(Math.random() * wordPool.length)];
                selectedWords.push(nextWord.charAt(0).toUpperCase() + nextWord.slice(1));
                i++; // NOSONAR - intentional double-increment to account for manually pushed word
              }
            }
          }
          // Add commas with some probability in the middle of sentences
          else if (currentSentenceLength > 2 && Math.random() < 0.1) {
            const lastIndex = selectedWords.length - 1;
            selectedWords[lastIndex] = selectedWords[lastIndex] + ',';
          }
        }
        
        // Add numbers if enabled (with lower probability)
        if (config.numbers && Math.random() < 0.05) {
          const numberTypes = ['integer', 'decimal', 'formatted'];
          const numberType = numberTypes[Math.floor(Math.random() * numberTypes.length)];
          let randomNumber = '';
          
          switch (numberType) {
            case 'integer':
              randomNumber = Math.floor(Math.random() * 1000).toString();
              break;
            case 'decimal':
              randomNumber = (Math.random() * 100).toFixed(2);
              break;
            case 'formatted':
              randomNumber = new Intl.NumberFormat().format(Math.floor(Math.random() * 1000000));
              break;
          }
          
          selectedWords.push(randomNumber);
        }
      }
      
      wordsArray = selectedWords;
      text = selectedWords.join(' ');
      break;
    }
    case 'code': {
      // Select a code snippet from the chosen language
      const languageId = config.language?.id || 'javascript';
      const availableSnippets = codeSnippets[languageId as keyof typeof codeSnippets];
      
      if (availableSnippets && availableSnippets.length > 0) {
        // Use selected language
        const randomSnippet = availableSnippets[Math.floor(Math.random() * availableSnippets.length)];
        text = randomSnippet.toString();
      } else {
        // Fallback to JavaScript if language not found or no snippets available
        text = codeSnippets.javascript[Math.floor(Math.random() * codeSnippets.javascript.length)];
      }
      
      // Split into words
      wordsArray = text.split(/\s+/);
      break;
    }
  }
  
  return { text, words: wordsArray };
}
